import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRightIcon, SearchIcon } from '@primer/octicons-react';
import { Portal } from '@primer/react';
import { usePackState } from '../../data/pack-store';
import { StatusIcon } from '../icons/StatusIcon';
import { buildPaletteItems, rankItems } from './items';
import styles from './palette.module.css';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
var isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.platform);
export var PALETTE_SHORTCUT_LABEL = isMac ? '⌘K' : 'Ctrl+K';
function LeadingVisual(_ref) {
  var _item$status;
  var item = _ref.item;
  if (item.kind === 'tab' || item.kind === 'action') {
    return /*#__PURE__*/_jsx(ArrowRightIcon, {
      size: 16,
      fill: "#636C76",
      "aria-hidden": "true"
    });
  }
  if ((item.kind === 'test' || item.kind === 'issue') && item.status) {
    return /*#__PURE__*/_jsx(StatusIcon, {
      status: item.status,
      size: 16
    });
  }
  // Steps: status-colored mini dot.
  return /*#__PURE__*/_jsx("span", {
    className: "".concat(styles.stepDot, " ").concat(styles["stepDot_".concat((_item$status = item.status) !== null && _item$status !== void 0 ? _item$status : 'unknown')]),
    "aria-hidden": "true"
  });
}

/**
 * ⌘K command palette (Figma node 30961:19931 + the prototype's chrome):
 * fuzzy search over tabs, tests, steps and actions, with a pack-aware
 * Suggested view when the query is empty. Available on both the run view
 * and the test detail.
 */
export function CommandPalette(_ref2) {
  var open = _ref2.open,
    onClose = _ref2.onClose,
    goTab = _ref2.goTab;
  var rowIds = usePackState(function (s) {
    return s.rowIds;
  });
  var rows = usePackState(function (s) {
    return s.rows;
  });
  var detail = usePackState(function (s) {
    return s.detail;
  });
  var attention = usePackState(function (s) {
    return s.attention;
  });
  var coverage = usePackState(function (s) {
    return s.coverage;
  });
  var _useState = useState(''),
    _useState2 = _slicedToArray(_useState, 2),
    query = _useState2[0],
    setQuery = _useState2[1];
  var _useState3 = useState(0),
    _useState4 = _slicedToArray(_useState3, 2),
    active = _useState4[0],
    setActive = _useState4[1];
  var inputRef = useRef(null);
  var listRef = useRef(null);
  var items = useMemo(function () {
    return buildPaletteItems({
      rowIds: rowIds,
      rows: rows,
      detail: detail,
      attention: attention,
      coverage: coverage,
      goTab: goTab
    });
  }, [rowIds, rows, detail, attention, coverage, goTab]);
  var ranked = useMemo(function () {
    return rankItems(items, query);
  }, [items, query]);

  // Fresh state per open; focus the input.
  useEffect(function () {
    if (open) {
      setQuery('');
      setActive(0);
      // After the dialog mounts.
      requestAnimationFrame(function () {
        var _inputRef$current;
        return (_inputRef$current = inputRef.current) === null || _inputRef$current === void 0 ? void 0 : _inputRef$current.focus();
      });
    }
  }, [open]);
  useEffect(function () {
    setActive(0);
  }, [query]);

  // Keep the active row in view.
  useEffect(function () {
    var _listRef$current;
    (_listRef$current = listRef.current) === null || _listRef$current === void 0 || (_listRef$current = _listRef$current.querySelector("[data-palette-index=\"".concat(active, "\"]"))) === null || _listRef$current === void 0 || _listRef$current.scrollIntoView({
      block: 'nearest'
    });
  }, [active, ranked]);
  if (!open) return null;
  var pick = function pick(item) {
    onClose();
    item.run();
  };
  var onKeyDown = function onKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation(); // the detail's Escape ladder must not fire
      onClose();
      return;
    }
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      var delta = event.key === 'ArrowDown' ? 1 : -1;
      setActive(function (i) {
        return (i + delta + ranked.length) % Math.max(ranked.length, 1);
      });
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      var item = ranked[active];
      if (item) pick(item);
    }
  };

  // Group consecutive items under their section headers.
  var lastSection = null;
  return (
    /*#__PURE__*/
    // Portaled like the RCA drawer: overlays live at body level, above
    // the isolated .page stacking context (palette 100 > drawer 90).
    _jsx(Portal, {
      children: /*#__PURE__*/_jsx("div", {
        className: styles.overlay,
        onMouseDown: onClose,
        onKeyDown: onKeyDown,
        children: /*#__PURE__*/_jsxs("div", {
          className: styles.dialog,
          role: "dialog",
          "aria-modal": "true",
          "aria-label": "Search tests, tabs, actions",
          onMouseDown: function onMouseDown(e) {
            return e.stopPropagation();
          },
          children: [/*#__PURE__*/_jsxs("div", {
            className: styles.inputRow,
            children: [/*#__PURE__*/_jsx(SearchIcon, {
              size: 16,
              fill: "#636C76",
              "aria-hidden": "true"
            }), /*#__PURE__*/_jsx("input", {
              ref: inputRef,
              className: styles.input,
              type: "text",
              role: "combobox",
              "aria-expanded": "true",
              "aria-controls": "ev-palette-list",
              "aria-activedescendant": ranked[active] ? "ev-palette-item-".concat(ranked[active].id) : undefined,
              placeholder: "Search tests, tabs, actions...",
              value: query,
              onChange: function onChange(e) {
                return setQuery(e.target.value);
              }
            }), /*#__PURE__*/_jsx("span", {
              className: styles.escChip,
              "aria-hidden": "true",
              children: "esc"
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.list,
            id: "ev-palette-list",
            role: "listbox",
            "aria-label": "Results",
            ref: listRef,
            children: [ranked.length === 0 && /*#__PURE__*/_jsxs("div", {
              className: styles.empty,
              children: ["No matches for \u201C", query.trim(), "\u201D"]
            }), ranked.map(function (item, i) {
              var showHeader = query.trim() === '' && item.section !== lastSection;
              lastSection = item.section;
              return /*#__PURE__*/_jsxs(React.Fragment, {
                children: [showHeader && /*#__PURE__*/_jsx("div", {
                  className: styles.sectionHeader,
                  children: item.section
                }), /*#__PURE__*/_jsxs("div", {
                  id: "ev-palette-item-".concat(item.id),
                  "data-palette-index": i,
                  role: "option",
                  "aria-selected": i === active,
                  className: "".concat(styles.row, " ").concat(i === active ? styles.rowActive : ''),
                  onMouseEnter: function onMouseEnter() {
                    return setActive(i);
                  },
                  onClick: function onClick() {
                    return pick(item);
                  },
                  children: [/*#__PURE__*/_jsx("span", {
                    className: styles.rowIcon,
                    children: /*#__PURE__*/_jsx(LeadingVisual, {
                      item: item
                    })
                  }), /*#__PURE__*/_jsx("span", {
                    className: styles.rowLabel,
                    children: item.label
                  }), item.hint && /*#__PURE__*/_jsx("span", {
                    className: styles.rowHint,
                    children: item.hint
                  }), i === active && /*#__PURE__*/_jsx("span", {
                    className: styles.rowEnter,
                    "aria-hidden": "true",
                    children: "\u21B5"
                  })]
                })]
              }, item.id);
            })]
          }), /*#__PURE__*/_jsxs("div", {
            className: styles.footer,
            children: [/*#__PURE__*/_jsxs("span", {
              className: styles.footerKeys,
              children: [/*#__PURE__*/_jsx("kbd", {
                className: styles.kbd,
                children: "\u2191"
              }), /*#__PURE__*/_jsx("kbd", {
                className: styles.kbd,
                children: "\u2193"
              }), " navigate", /*#__PURE__*/_jsx("kbd", {
                className: "".concat(styles.kbd, " ").concat(styles.kbdGap),
                children: "\u21B5"
              }), " select"]
            }), /*#__PURE__*/_jsxs("span", {
              className: styles.footerRight,
              children: [PALETTE_SHORTCUT_LABEL, " anywhere"]
            })]
          })]
        })
      })
    })
  );
}